import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Circle from '~/components/Circle'
import Text, { FontStyle } from '~/components/core/Text'
import FillSliderWithCuts from '~/components/FillSliderWithCuts'
import { CallTranscript } from '~/types/CallTranscript'
import { isBetween } from '~/utils/math'

interface InterruptionsStatWrapperProps {
  $width: number
  $margin?: string
}

const HoldTimeAndPausesStatWrapper = styled.div<InterruptionsStatWrapperProps>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.$width}px;
  margin: ${(props) => props.$margin ?? ''};
`

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 15px;
  margin-bottom: 1px;
`

interface InterruptionsStatProps {
  transcript: CallTranscript
  width?: number
  margin?: string
}

const InterruptionsStat = ({
  transcript,
  width,
  margin,
}: InterruptionsStatProps) => {
  const { t } = useTranslation()
  //based on google... 170 wpm is average-high.  Let's pad this to 225 words per minute as the total amount of time
  const totalInterruptions =
    transcript.ConversationCharacteristics.Interruptions.TotalCount

  const agentInterruptions =
    transcript.ConversationCharacteristics.Interruptions
      .InterruptionsByInterrupter.AGENT?.length ?? 0

  const customerInterruptions =
    transcript.ConversationCharacteristics.Interruptions
      .InterruptionsByInterrupter.CUSTOMER?.length ?? 0

  const agentInterruptionPercentage =
    agentInterruptions <= 0 ? 0 : agentInterruptions / totalInterruptions
  const customerInterruptionPercentage =
    customerInterruptions <= 0 ? 0 : customerInterruptions / totalInterruptions

  const neutralColor = 'var(--chakra-colors-bg-muted)'
  const customerColor = 'var(--chakra-colors-fg-muted)'

  return (
    <HoldTimeAndPausesStatWrapper $width={width ?? 300} $margin={margin}>
      <Text fontStyle={FontStyle.ButtonBold}>
        {t(
          'callAnalytics.stats.interruptionsTotal',
          'Interruptions: {{count}} total',
          { count: totalInterruptions },
        )}
      </Text>
      <FillSliderWithCuts
        height={14}
        width={width ?? 300}
        fillPercentage={agentInterruptionPercentage}
        fillColor={neutralColor}
        backgroundColor="var(--chakra-colors-bg-subtle)"
        borderColor="var(--chakra-colors-border-default)"
        border="1px solid"
        borderRadius={4}
        fillBorderRadius={
          isBetween(agentInterruptionPercentage, Number.EPSILON, 1)
            ? '4px 0px 0px 4px'
            : 4
        }
        margin="4px 0px 3px 0px"
        animate
        cuts={Math.max(totalInterruptions - 1, 0)}
        cutInterval={100 / totalInterruptions}
        cutWidth={2}
        cutColor="var(--chakra-colors-bg-subtle)"
      />
      <StatsWrapper>
        <Circle radius={6} color={neutralColor} margin="0px 5px 0px 0px" />
        <Text fontStyle={FontStyle.TinySmall}>
          {t(
            'callAnalytics.stats.interruptionsAgent',
            'Agent: {{count}} ({{percent}}%)',
            {
              count: agentInterruptions,
              percent: Math.round(agentInterruptionPercentage * 100).toFixed(0),
            },
          )}
        </Text>
      </StatsWrapper>
      <StatsWrapper>
        <Circle radius={6} color={customerColor} margin="0px 5px 0px 0px" />
        <Text fontStyle={FontStyle.TinySmall}>
          {t(
            'callAnalytics.stats.interruptionsCustomer',
            'Customer: {{count}} ({{percent}}%)',
            {
              count: customerInterruptions,
              percent: Math.round(customerInterruptionPercentage * 100).toFixed(
                0,
              ),
            },
          )}
        </Text>
      </StatsWrapper>
    </HoldTimeAndPausesStatWrapper>
  )
}

export default InterruptionsStat
